import * as _ from 'lodash';
import * as numeral from 'numeral';

require('numeral/locales.js');
numeral.locale('pt-br');

export interface INumeralService {
  parseToNumeral(value: number): Numeral;

  parseToNumber(value: string | number): number;

  parseNumberToString(value: string | number, format: string): string;

  getMax(values: Array<any | number>, property: string, variation: number): number;

  getMaxFromProperties(values: Array<any | number>, properties: Array<string>, variation: number, ceil: boolean): number;

  getMin(values: Array<any | number>, property: string, variation: number): number;

  getMinFromProperties(values: Array<any | number>, properties: Array<string>, variation: number, ceil: boolean): number;

  getRangeScaleFromProperties(values: Array<any | number>, properties: Array<string>, scale: number): number;

  hasValuesFromProperties(values: Array<any | number>, properties: Array<string>): boolean;
}

export class NumeralService implements INumeralService {
  parseToNumeral(value: number): Numeral {
    return numeral(value);
  }

  parseToNumber(value: string | number): number {
    return numeral(value).value();
  }

  parseNumberToString(value: string | number, format: string = '0,0'): string {
    return numeral(value).format(format);
  }

  getMax(values: Array<any | number>, property: string, variation = 1, ceil = true) {
    if (!values) {
      return 0;
    }

    if (!property) {
      return _.max(values);
    }

    const maxValue = (_.maxBy(values, v => v && v[property]) || {})[property];
    const value = (ceil ? Math.ceil(maxValue) : maxValue) * variation;
    return isNaN(value) ? 0 : value;
  }

  getMaxFromProperties(values: Array<any | number>, properties: Array<string>, variation = 1, ceil = true) {
    if (!_.size(values)) {
      return 0;
    }

    const maxValues = _.map(properties, (property) => (_.maxBy(values, v => v && v[property]) || {})[property]);
    const value = (ceil ? Math.ceil(_.max(maxValues)) : _.max(maxValues)) * variation;
    return isNaN(value) ? 0 : value;
  }

  getRangeScaleFromProperties(values: Array<any | number>, properties: Array<string>, scale = 0) {
    if (!_.size(values)) {
      return 0;
    }

    const maxValues = _.map(properties, (property) => (_.maxBy(values, v => v && v[property]) || {})[property]);
    const maxValue = _.max(maxValues);

    const minValues = _.map(properties, (property) => (_.minBy(values, v => v && v[property]) || {})[property]);
    const minValue = _.min(minValues);

    if (isNaN(maxValue) || isNaN(minValue)) {
      return 0;
    }

    const range = (maxValue - minValue) * scale;

    if (scale < 0) {
      return minValue + range;
    } else {
      return maxValue + range;
    }
  }

  getMin(values: Array<any | number>, property: string, variation = 1, floor = true) {
    if (!values) {
      return 0;
    }

    if (!property) {
      return _.min(values);
    }

    const minValue = (_.minBy(values, v => v && v[property]) || {})[property];
    const value = (floor ? Math.floor(minValue) : minValue) * variation;
    return isNaN(value) ? 0 : value;
  }

  getMinFromProperties(values: Array<any | number>, properties: Array<string>, variation = 1, floor = true) {
    if (!_.size(values)) {
      return 0;
    }

    const minValues = _.map(properties, (property) => (_.minBy(values, v => v && v[property]) || {})[property]);
    const minValue = (floor ? Math.floor(_.min(minValues)) : _.min(minValues));
    const value = minValue - Math.abs(minValue * variation);
    return isNaN(value) ? 0 : value;
  }

  hasValuesFromProperties(values: Array<any | number>, properties: Array<string>) {
    if (!_.size(values)) {
      return false;
    }

    return _.some(properties, (property) => _.some(values, v => v[property] !== undefined));
  }
}

export const numeralService = new NumeralService();
